/**
 * Manipulação do `pathFile` — o caminho lógico do arquivo, com pasta e extensão.
 *
 * Não existe pasta como entidade no backend: a lista de pastas da tela é derivada varrendo o
 * `pathFile` dos arquivos. Renomear e mover, portanto, são a mesma operação — reescrever essa
 * string — e é por isso que a composição mora aqui, testada, em vez de espalhada pelos
 * componentes.
 *
 * Os caminhos chegam do Vision com prefixo de disco (`C:/FuelTech Vision/...`) e barras
 * invertidas. Tudo é normalizado para o formato lógico (`Maps/celta.ftmap`) antes de compor.
 */

/** Tira prefixo de disco, o "FuelTech Vision" e uniformiza as barras. */
function normalize(pathFile?: string | null): string {
  if (!pathFile) return '';
  return pathFile
    .replace(/\\/g, '/')
    .replace(/^[A-Za-z]:\//, '')
    .replace(/^\/+/, '')
    .replace(/^FuelTech Vision\//i, '');
}

/** Pasta lógica do arquivo; vazia quando ele está na raiz. */
export function folderOf(pathFile?: string | null): string {
  const normalized = normalize(pathFile);
  const lastSlash = normalized.lastIndexOf('/');
  return lastSlash >= 0 ? normalized.slice(0, lastSlash) : '';
}

/** Nome do arquivo, com extensão. */
export function fileNameOf(pathFile?: string | null): string {
  const normalized = normalize(pathFile);
  const lastSlash = normalized.lastIndexOf('/');
  return lastSlash >= 0 ? normalized.slice(lastSlash + 1) : normalized;
}

/** Limpa uma pasta digitada pelo usuário; vazio significa raiz. */
export function sanitizeFolder(folder: string): string {
  // O trim vem primeiro: com espaço nas pontas, as âncoras ^ e $ da limpeza de barras não
  // casam, e o caminho sai com a barra que deveria ter sido tirada.
  return folder
    .trim()
    .replace(/\\/g, '/')
    .replace(/\/{2,}/g, '/')
    .replace(/^\/+|\/+$/g, '');
}

/** Extensão com o ponto (`.ftmap`), ou vazio se o nome não tiver uma. */
function extensionOf(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(lastDot) : '';
}

const join = (folder: string, fileName: string) => (folder ? `${folder}/${fileName}` : fileName);

/**
 * Troca o nome do arquivo, preservando pasta e extensão.
 *
 * A extensão só é acrescentada quando o nome digitado ainda não termina com ela. Cortar tudo
 * depois do último ponto seria mais simples e estaria errado: "celta 2.0 turbo" viraria
 * "celta 2". Como consequência assumida, quem digita outra extensão fica com as duas — o
 * diálogo resolve isso mostrando a extensão como sufixo fixo, fora do campo.
 *
 * Nome vazio devolve o caminho original: renomear para nada não é uma operação.
 */
export function renamePath(pathFile: string | null | undefined, newName: string): string {
  const folder = folderOf(pathFile);
  const currentName = fileNameOf(pathFile);
  const trimmed = newName.trim();
  if (!trimmed) return join(folder, currentName);

  const ext = extensionOf(currentName);
  const alreadyHasExt = ext && trimmed.toLowerCase().endsWith(ext.toLowerCase());
  return join(folder, alreadyHasExt ? trimmed : `${trimmed}${ext}`);
}

/** Leva o arquivo para outra pasta, mantendo o nome. Pasta vazia = raiz. */
export function movePath(pathFile: string | null | undefined, targetFolder: string): string {
  return join(sanitizeFolder(targetFolder), fileNameOf(pathFile));
}
